import { useCallback, useMemo } from 'react';

import { useLocale } from '../../../hooks/useLocale';
import { logToNewRelic } from '../../../services/debug';
import { useFlag } from '../../../services/launchDarkly';
import type { EventTriggerPopupData } from '../../../stores/event-trigger/types';
import { RoadLocalizedAssetKey } from '../../../stores/event-trigger/types';
import { useStores } from '../../../stores/useStores';
import { getErrorMessage } from '../../../utils/errors';
import { isQuestEventTrigger, isRoadEventTrigger } from '../../../utils/events';
import { getImageWidgetUrl, getRoadLocalizedUrl } from '../../../utils/localizationUtils';

interface UseEventTriggerWidgetDataProps {
  event: EventTriggerPopupData;
}

export function useEventTriggerWidgetData(props: UseEventTriggerWidgetDataProps) {
  const { event } = props;
  const { eventTriggerStore } = useStores();
  const { locale } = useLocale();
  const offerLocalizationFF = useFlag('offer_localization', false);
  const messageLocalizationFF = useFlag('message_localization', false);

  const widgetImageUrl = useMemo(() => {
    if (isQuestEventTrigger(event.benefit)) {
      return event.benefit.widgetImageUrl;
    }

    if (isRoadEventTrigger(event.benefit)) {
      return getRoadLocalizedUrl(event.benefit.assets, RoadLocalizedAssetKey.WIDGET, locale);
    }

    return getImageWidgetUrl({
      locale,
      benefit: event.benefit,
      offerLocalizationFF,
      messageLocalizationFF,
    });
  }, [event, locale, messageLocalizationFF, offerLocalizationFF]);

  const handleClick = useCallback(() => {
    try {
      eventTriggerStore.sendAnalyticsEvents(
        {
          event_action: 'impression',
          triggered_by: 'click',
          click_location: 'widget',
        },
        event,
      );
      void eventTriggerStore.openPopupByUserEventTriggerId(event.userEventTriggerId);
    } catch (e) {
      const errorMessage = getErrorMessage(e);
      logToNewRelic('Widget click error', errorMessage, 'error');
    }
  }, [event, eventTriggerStore]);

  return { widgetImageUrl, handleClick };
}
